import { createAction } from '@reduxjs/toolkit';
import type { TemporalWorkflowDocument } from 'src/content/Workflows/data/workflowTypes';

export type TriggerSavePayload = { label: string; pdfPath: string };
export type AgentSavePayload = {
  label: string;
  config: Record<string, unknown>;
};

export type SerializedAgentTool = {
  rid: number;
  code: string;
  name?: string;
  category_code: string;
  r_type: 'ACTION';
  connector_id?: string | number;
  r_settings?: Record<string, unknown> | null;
};

export const setAgentToolsAction = createAction<{
  workflowId: string;
  nodeId: string;
  tools: SerializedAgentTool[];
}>('workflowBuilder/setAgentTools');

/**
 * Replaces the whole per-node tool map in one dispatch. Used by change-log
 * reverts and undo/redo, which restore a captured snapshot of every node's
 * tools at once (a per-node loop would bump `docKey` once per node).
 */
export const setAllAgentToolsAction = createAction<{
  workflowId: string;
  toolsByNodeId: Record<string, SerializedAgentTool[]>;
}>('workflowBuilder/setAllAgentTools');

export const enrichToolsConnectorAction = createAction<{
  workflowId: string;
  actionIds: number[];
  connectorId: number;
}>('workflowBuilder/enrichToolsConnector');

export const clearToolsConnectorAction = createAction<{
  workflowId: string;
  categoryCode: string;
}>('workflowBuilder/clearToolsConnector');

/** One action → both slices update atomically via `extraReducers`. */
export const hydrateWorkflowDoc = createAction<{
  workflowId: string;
  doc: TemporalWorkflowDocument;
}>('workflowBuilder/hydrateWorkflowDoc');

export const seedTemplateDraft = createAction<{
  workflowId: string;
  doc: TemporalWorkflowDocument;
}>('workflowBuilder/seedTemplateDraft');

export const saveWorkflowIdAction = createAction<{
  workflowId: string;
  newId: string;
}>('workflowBuilder/saveWorkflowId');

export const saveTriggerAction = createAction<{
  workflowId: string;
  triggerId: string;
  payload: TriggerSavePayload;
}>('workflowBuilder/saveTrigger');

export const saveAgentRootAction = createAction<{
  workflowId: string;
  nodeId: string;
  payload: AgentSavePayload;
}>('workflowBuilder/saveAgentRoot');

export const saveAgentNestedAction = createAction<{
  workflowId: string;
  loopId: string;
  nodeId: string;
  payload: AgentSavePayload;
}>('workflowBuilder/saveAgentNested');

export const patchAgentLiveRootAction = createAction<{
  workflowId: string;
  nodeId: string;
  payload: AgentSavePayload;
}>('workflowBuilder/patchAgentLiveRoot');

export const patchAgentLiveNestedAction = createAction<{
  workflowId: string;
  loopId: string;
  nodeId: string;
  payload: AgentSavePayload;
}>('workflowBuilder/patchAgentLiveNested');

export const saveNodeConfigAction = createAction<{
  workflowId: string;
  nodeId: string;
  patch: Record<string, unknown>;
}>('workflowBuilder/saveNodeConfig');

export const saveMainAction = createAction<{
  workflowId: string;
  doc: TemporalWorkflowDocument;
}>('workflowBuilder/saveMain');

export const resetLayoutAction = createAction<{
  workflowId: string;
  doc: TemporalWorkflowDocument;
}>('workflowBuilder/resetLayout');

export const resetForWorkflowAction = createAction<{ workflowId: string }>(
  'workflowBuilder/resetForWorkflow'
);

/**
 * Moves a workflow's builder state from one bucket key to another, used when an
 * unsaved draft receives its persisted id (see `buildWorkflowScopeKey`). Without
 * it the freshly-keyed bucket would start empty and the draft the user is
 * looking at — canvas, drawer state, MCP tools — would be dropped mid-session.
 * No-op when `from` has no state or `to` already has some.
 */
export const renameWorkflowScopeAction = createAction<{
  from: string;
  to: string;
}>('workflowBuilder/renameWorkflowScope');
